import { AuctionMode } from "../types/auction";

// Frontend mirror of Auction_Development/utils/auctionDirection.js -- keeps
// bid-direction logic (reverse: lowest wins / forward: highest wins) in one
// place instead of scattered comparisons across bidding components.

export function normaliseMode(mode?: AuctionMode | string | null): AuctionMode {
  return mode === "forward" ? "forward" : "reverse";
}

export function isForwardMode(mode?: AuctionMode | string | null): boolean {
  return normaliseMode(mode) === "forward";
}

// Picks the best value out of a list of numeric amounts.
export function pickBest(mode: AuctionMode | string | undefined, amounts: number[]): number | null {
  if (amounts.length === 0) return null;
  return isForwardMode(mode) ? Math.max(...amounts) : Math.min(...amounts);
}

// The boundary a new bid must cross to be accepted, given the reference
// amount (current best) and the configured step (decrement/increment).
// Reverse: new bid must be <= reference - step (a ceiling).
// Forward:  new bid must be >= reference + step (a floor).
export function nextBoundary(mode: AuctionMode | string | undefined, referenceAmount: number, step: number): number {
  return isForwardMode(mode) ? referenceAmount + step : referenceAmount - step;
}

// Whether `amount` violates the boundary (i.e. the bid should be rejected).
export function violatesBoundary(mode: AuctionMode | string | undefined, amount: number, boundary: number): boolean {
  return isForwardMode(mode) ? amount < boundary : amount > boundary;
}

export function boundaryLabel(mode: AuctionMode | string | undefined): "lowest" | "highest" {
  return isForwardMode(mode) ? "highest" : "lowest";
}

// Rank labels follow the auction's direction: reverse auctions rank by the
// lowest bid (L1, L2, L3...), forward auctions by the highest (H1, H2, H3...).
export function rankPrefix(mode: AuctionMode | string | undefined | null): "H" | "L" {
  return isForwardMode(mode) ? "H" : "L";
}

export function rankLabel(mode: AuctionMode | string | undefined | null, rank: number | string): string {
  return `${rankPrefix(mode)}${rank}`;
}

export function stepFieldLabel(mode: AuctionMode | string | undefined): "Incremental Value" | "Decremental Value" {
  return isForwardMode(mode) ? "Incremental Value" : "Decremental Value";
}
